import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import BrandHeader from "../../components/BrandHeader";
import { inventoryApi } from "../../lib/api";
import { crystalColor } from "../../lib/crystalColors";
import { ELEMENT_ORDER, CRYSTAL_ELEMENT, ELEMENT_STYLES } from "../../lib/constants";
import { ELEMENTS } from "../../lib/panchaEngine";

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    inventoryApi.list().then((l) => { setItems(l); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const updateField = async (id, patch) => {
    setSavingId(id);
    try {
      const updated = await inventoryApi.update(id, patch);
      setItems((list) => list.map((i) => (i.id === id ? updated : i)));
    } catch {
      toast.error("Update failed");
    }
    setSavingId(null);
  };

  const grouped = useMemo(() => {
    const g = {};
    ELEMENT_ORDER.forEach((el) => { g[el] = []; });
    g.other = [];
    items.forEach((i) => {
      const el = CRYSTAL_ELEMENT[i.crystalName];
      if (el && g[el]) g[el].push(i);
      else g.other.push(i);
    });
    Object.keys(g).forEach((k) => g[k].sort((a, b) => a.crystalName.localeCompare(b.crystalName)));
    return g;
  }, [items]);

  const lowCount = items.filter((i) => i.stock <= i.reorderLevel).length;
  const lowItems = items.filter((i) => i.stock <= i.reorderLevel);

  return (
    <div className="min-h-screen" data-testid="inventory-page">
      <BrandHeader adminMode />
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="ph-overline">Inventory</div>
            <h1 className="font-serif text-4xl text-[#2C0A5E] mt-2">Crystal Stock by Element</h1>
          </div>
          <div className="ph-card px-5 py-3">
            <div className="ph-overline">Low Stock</div>
            <div className="font-serif text-2xl text-[#b91c1c] mt-1" data-testid="low-stock-count">{lowCount}</div>
          </div>
        </div>

        {lowItems.length > 0 && (
          <div
            className="rounded-xl px-5 py-4 mb-8"
            style={{
              background: "linear-gradient(135deg, #FFF7E6 0%, #FDECC6 100%)",
              border: "1px solid #E0A030",
              color: "#7A4A00",
            }}
            data-testid="low-stock-banner"
          >
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center font-serif text-sm"
                style={{ background: "#E0A030", color: "#FFFFFF" }}
                aria-hidden
              >!
              </div>
              <div className="flex-1">
                <div className="font-serif text-lg leading-tight">
                  {lowItems.length} {lowItems.length === 1 ? "crystal is" : "crystals are"} at or below the reorder level
                </div>
                <div className="text-sm mt-1" style={{ color: "#8A5A10" }}>
                  {lowItems
                    .slice(0, 6)
                    .map((i) => `${i.crystalName} (${i.stock})`)
                    .join(" \u00b7 ")}
                  {lowItems.length > 6 ? ` \u00b7 +${lowItems.length - 6} more` : ""}
                </div>
                <div className="text-xs mt-2 italic" style={{ color: "#8A5A10" }}>
                  Restock any crystal above its reorder level to clear that alert. The owner has already been notified on WhatsApp.
                </div>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-[#6B5E7E]">Loading inventory...</p>
        ) : (
          <div className="space-y-10" data-testid="inventory-grouped">
            {ELEMENT_ORDER.map((el) => (
              <ElementRow
                key={el}
                element={el}
                sanskrit={ELEMENTS[el].sanskrit}
                items={grouped[el] || []}
                onUpdate={updateField}
                savingId={savingId}
              />
            ))}
            {grouped.other.length > 0 && (
              <ElementRow
                key="other"
                element="other"
                sanskrit="Unassigned"
                items={grouped.other}
                onUpdate={updateField}
                savingId={savingId}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ElementRow({ element, sanskrit, items, onUpdate, savingId }) {
  const style = ELEMENT_STYLES[element] || { bg: "#f5f5f5", border: "#ddd", text: "#333" };
  return (
    <section data-testid={`inventory-element-${element}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium"
          style={{ backgroundColor: style.bg, borderColor: style.border, color: style.text }}>
          <span className="capitalize">{element === "other" ? "Other" : element}</span>
          <span className="text-[10px] tracking-[0.2em] uppercase opacity-80">{sanskrit}</span>
        </span>
        <span className="text-sm text-[#6B5E7E]">{items.length} crystals</span>
        <div className="flex-1 h-px bg-[rgba(106,13,173,0.1)]" />
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[#6B5E7E] italic pl-1">No crystals in this element.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => {
            const low = item.stock <= item.reorderLevel;
            return (
              <div key={item.id} className={`ph-card p-5 ${low ? "ring-1 ring-[#ef4444]/40" : ""}`} data-testid={`inventory-item-${item.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bead shrink-0" style={{ backgroundColor: crystalColor(item.crystalName) }} />
                  <div className="flex-1">
                    <div className="font-serif text-lg text-[#2C0A5E]">{item.crystalName}</div>
                    {low && <div className="text-xs text-[#b91c1c] font-medium mt-0.5">Low stock - reorder soon</div>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div>
                    <label className="ph-overline">Stock</label>
                    <input
                      type="number"
                      className="ph-input mt-1 text-sm"
                      defaultValue={item.stock}
                      min="0"
                      onBlur={(e) => onUpdate(item.id, { stock: parseInt(e.target.value || "0", 10) })}
                      data-testid={`stock-${item.id}`}
                    />
                  </div>
                  <div>
                    <label className="ph-overline">Reorder at</label>
                    <input
                      type="number"
                      className="ph-input mt-1 text-sm"
                      defaultValue={item.reorderLevel}
                      min="0"
                      onBlur={(e) => onUpdate(item.id, { reorderLevel: parseInt(e.target.value || "0", 10) })}
                      data-testid={`reorder-${item.id}`}
                    />
                  </div>
                </div>
                {savingId === item.id && <div className="text-xs text-[#6A0DAD] mt-2">Saving...</div>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
